import React, {useEffect, useRef, useState, useCallback, useMemo} from "react";
import {connect} from "react-redux";
import {Link, useRouteMatch} from "react-router-dom";
import {FormattedMessage} from "react-intl";

// @mui/material components
import {Box} from "@mui/material";

// @mui/icons-material
import {NotificationsActive} from "@mui/icons-material";

// core components
import Snackbar from "components/snackbar/snackbar";
import GridContainer from "components/grid/gridContainer";
import GridItem from "components/grid/gridItem";
import Card from "components/card/card";
import CardHeader from "components/card/cardHeader";
import CardBody from "components/card/cardBody";
import CardFooter from "components/card/cardFooter";
import PaginationContainer from "components/pagination/paginationContainer";
import ProductMappingStats from "components/ProductMappingStats";
import SearchBar from "components/List/components/SearchBar";
import CustomButton from "components/CustomButton";

// local components
import TableDemo from "./tableDemo";
import DeleteRetailerDialog from "./components/DeleteRetailerDialog";

// utils
import {Authorizations, hasAuthorization} from "utils/authorizations";
import {capitalize} from "utils/capitalize";

// actions
import {list, reset} from "actions/retailer/list";
import {create} from "actions/retailer/stripe/create";
import {del} from "actions/retailer/delete";

const containerFluidStyle = {
    paddingRight: "15px",
    paddingLeft: "15px",
    marginRight: "auto",
    marginLeft: "auto",
    width: "100%",
};

const snackbarMessageStyle = {
    display: "flex",
    alignItems: "center",
};

function ListDemo(props) {
    const {
        retrieved,
        loading,
        error,
        deletedItem,
        deleteLoading,
        stripeInvited,
        created,
        updated,
        authorizations,
        list: listAction,
        create: createStripeAction,
        del: delAction,
        reset: resetAction,
        resetUpdate,
    } = props;

    const match = useRouteMatch();

    const [errorNotification, setErrorNotification] = useState(true);
    const [deletedNotification, setDeletedNotification] = useState(true);
    const [updatedNotification, setUpdatedNotification] = useState(true);
    const [createdNotification, setCreatedNotification] = useState(true);
    const [stripeInvitedNotification, setStripeInvitedNotification] = useState(true);
    const [search, setSearch] = useState("");

    const params = match.params.params;

    useEffect(() => {
        listAction(params && decodeURIComponent(params));
    }, [listAction, params, deletedItem]);

    const filteredData = useMemo(() => {
        const members = retrieved && retrieved["hydra:member"];
        if (!members || !search) return members;
        const lowerSearch = search.toLowerCase();
        return members.filter(retailer =>
            retailer.name && retailer.name.toLowerCase().includes(lowerSearch)
        );
    }, [retrieved, search]);

    useEffect(() => {
        setErrorNotification(true);
        setDeletedNotification(true);
    }, [error, deletedItem]);

    useEffect(() => {
        return () => {
            resetAction();
            resetUpdate();
        };
    }, [resetAction, resetUpdate]);

    const [retailerToDelete, setRetailerToDelete] = useState(null);

    const handleDelete = useCallback((item) => {
        setRetailerToDelete(item);
    }, []);

    const handleConfirmDelete = useCallback((item) => {
        delAction(item);
        setRetailerToDelete(null);
    }, [delAction]);

    const handleCreateStripe = useCallback((item) => {
        createStripeAction(item["id"]);
    }, [createStripeAction]);

    return (
        <div>
            <Box sx={{marginLeft: {md: "240px"}}}>
                <div style={{
                    ...containerFluidStyle,
                    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontWeight: "300",
                    lineHeight: "1.5em",
                }}>
                    {deletedItem && (
                        <Snackbar
                            open={deletedNotification}
                            close
                            closeNotification={() => setDeletedNotification(false)}
                            place={"bl"}
                            color={"success"}
                            message={
                                <div style={snackbarMessageStyle}>
                                    <NotificationsActive/>
                                    <FormattedMessage
                                        id={"retailer.notifications.retailerDeleted"}
                                        values={{name: capitalize(deletedItem["name"])}}
                                    />
                                </div>
                            }
                        />
                    )}

                    {error && (
                        <Snackbar
                            open={errorNotification}
                            close
                            closeNotification={() => setErrorNotification(false)}
                            place={"bl"}
                            color={"danger"}
                            message={
                                <div style={snackbarMessageStyle}>
                                    <NotificationsActive/> {error}
                                </div>
                            }
                        />
                    )}

                    {created && (
                        <Snackbar
                            open={createdNotification}
                            close
                            closeNotification={() => setCreatedNotification(false)}
                            place={"bl"}
                            color={"success"}
                            message={
                                <div style={snackbarMessageStyle}>
                                    <NotificationsActive/>
                                    <FormattedMessage
                                        id={"retailer.notifications.retailerCreated"}
                                        values={{name: capitalize(created["name"])}}
                                    />
                                </div>
                            }
                        />
                    )}

                    {updated && (
                        <Snackbar
                            open={updatedNotification}
                            close
                            closeNotification={() => setUpdatedNotification(false)}
                            place={"bl"}
                            color={"success"}
                            message={
                                <div style={snackbarMessageStyle}>
                                    <NotificationsActive/>
                                    <FormattedMessage
                                        id={"retailer.notifications.retailerUpdated"}
                                        values={{name: capitalize(updated["name"])}}
                                    />
                                </div>
                            }
                        />
                    )}

                    {stripeInvited && (
                        <Snackbar
                            open={stripeInvitedNotification}
                            close
                            closeNotification={() => setStripeInvitedNotification(false)}
                            place={"bl"}
                            color={"success"}
                            message={
                                <div style={snackbarMessageStyle}>
                                    <NotificationsActive/>
                                    <FormattedMessage id={"retailer.notifications.retailerInvited"}/>
                                </div>
                            }
                        />
                    )}

                    <GridContainer>
                        <GridItem xs={12}>
                            <Card style={{height: window.innerHeight * 0.94}}>
                                <CardHeader color="info">
                                    <GridContainer>
                                        <GridItem xs={5} sm={5}>
                                            <h3><FormattedMessage id={"retailer.list.title"}/></h3>
                                        </GridItem>
                                        {(hasAuthorization(authorizations, Authorizations.MARKETPLACE_RETAILER_MANAGEMENT) && ((!loading && !deleteLoading) || !retrieved)) &&
                                            <GridItem xs={7} sm={7} style={{margin: "8px 0px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "16px"}}>
                                                <div>
                                                    <SearchBar
                                                        customPlaceholder={"retailer.list.search.placeholder"}
                                                        onChange={(value) => setSearch(value)}
                                                        style={{maxWidth: '300px'}}
                                                    />
                                                </div>
                                                <Link to="create" style={{textDecoration: 'none'}}>
                                                    <CustomButton>
                                                        <FormattedMessage id="retailer.list.button.add" />
                                                    </CustomButton>
                                                </Link>
                                            </GridItem>
                                        }
                                    </GridContainer>
                                </CardHeader>
                                <CardBody style={{minHeight: 0, overflow: 'hidden', padding: 0}}>
                                    <div style={{height: '100%', overflowY: 'auto', padding: '0 1.875rem'}}>
                                        <div style={{marginTop: '32px', marginBottom: '32px'}}>
                                            <ProductMappingStats />
                                        </div>
                                        <TableDemo
                                            tableData={filteredData}
                                            match={match}
                                            del={handleDelete}
                                            createStripe={handleCreateStripe}
                                        />
                                    </div>
                                </CardBody>
                                <CardFooter center>
                                    <PaginationContainer
                                        itemsPerPage={500}
                                        urlParams={params}
                                        retrieved={retrieved}
                                    />
                                </CardFooter>
                            </Card>
                        </GridItem>
                    </GridContainer>
                </div>
            </Box>
            <DeleteRetailerDialog
                open={!!retailerToDelete}
                retailer={retailerToDelete}
                onClose={() => setRetailerToDelete(null)}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
}

const mapStateToProps = state => {
    const {
        retrieved,
        loading,
        error,
    } = state.retailer.list;

    const {
        deleted
    } = state.retailer.del;

    return {
        retrieved,
        loading,
        error,
        deleteLoading: state.retailer.del.loading,
        stripeLoading: state.retailer.invite.loading,
        stripeInvited: state.retailer.invite.invite,
        deletedItem: deleted,
        created: state.retailer.create.created,
        updated: state.retailer.update.updated,
        authorizations: state.authentication.authorizations,
    };
};

const mapDispatchToProps = dispatch => ({
    list: params => dispatch(list(params)),
    create: params => dispatch(create(params)),
    del: item => dispatch(del(item)),
    reset: () => dispatch(reset()),
    resetUpdate: () => dispatch({type: "RETAILER_UPDATE_RESET"}),
});

export default connect(mapStateToProps, mapDispatchToProps)(ListDemo);
